import JobListCard from "@legacy/jobs/components/JobListCard"
import { useState } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import extractURLSearchParam from "@utils/extractURLSearchParam"

import { usePreviewMode } from "@particles/PreviewMode/PreviewMode.context"

import { CardList, Table, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"

import { JobTableViewTabs } from "@pages/Jobs/JobList/views/JobTableView/JobTableViewTabs"
import JobPreviewCard from "@pages/Jobs/JobList/views/JobTimelineView/JobPreviewCard/JobPreviewCard"

import { JOBS_ROUTES } from "@routes/jobs"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    JOBS_LIST_CONTROLS_ACTIVE_TAB,
    JOBS_LIST_CONTROLS_KEYWORD_SEARCH,
    JOBS_LIST_CONTROLS_PHASE,
} from "@constants/storage"

import styles from "./JobCardsView.module.scss"

const JOB_CARDS_VIEW_ATTRIBUTES = [
    { value: "service_name", label: "Service" },
    { value: "labels", label: "Labels" },
    { value: "job_type", label: "Job Type" },
    { value: "priority", label: "Priority" },
    { value: "origin_type_label", label: "Source" },
    { value: "assigned_technicians", label: "Techs" },
    { value: "client_name", label: "Client" },
    { value: "service_location_name", label: "Location Name" },
    { value: "service_location_address", label: "Location Address" },
    { value: "start_time", label: "Scheduled For" },
    { value: "duration", label: "Duration" },
]

const JOB_CARDS_VIEW_SORT_OPTIONS = [
    { value: "id", label: "ID" },
    { value: "service_name", label: "Service" },
    { value: "status_label", label: "Status" },
    { value: "priority", label: "Priority" },
    { value: "origin_type_label", label: "Source" },
    { value: "origin_id", label: "Source ID" },
    { value: "date_received", label: "Date Received" },
    { value: "client_name", label: "Client" },
    { value: "service_location_name", label: "Location Name" },
    { value: "service_location_address", label: "Location Address" },
    { value: "start_time", label: "Scheduled For" },
    { value: "duration", label: "Duration" },
    { value: "last_scheduled_at", label: "Scheduled" },
    { value: "last_modified_at", label: "Last Modified" },
    { value: "last_active_at", label: "Active" },
    { value: "last_paused_at", label: "Paused" },
    { value: "last_on_hold_at", label: "On Hold" },
    { value: "last_completed_at", label: "Completed" },
    { value: "last_finalized_at", label: "Finalized" },
    { value: "last_cancelled_at", label: "Cancelled" },
]

export default function JobCardsView() {
    const [createRoute] = useSlugExtractor([JOBS_ROUTES.CREATE])
    const { hasPermission } = useHasAccess()

    const seriesFilter = extractURLSearchParam("filter__series", false)
    const seriesSearch = new URLSearchParams()

    if (seriesFilter) {
        seriesSearch.set("series", seriesFilter)
    }

    const [hoveredJobId, setHoveredJobId] = useState<Job["id"]>("")
    const { isPreviewModeEnabled } = usePreviewMode()

    return (
        <div className={styles.base} onMouseLeave={() => setHoveredJobId("")}>
            <Table.Provider
                activePhaseStorageKey={JOBS_LIST_CONTROLS_PHASE}
                activeTabStorageKey={JOBS_LIST_CONTROLS_ACTIVE_TAB}
                searchKeywordsStorageKey={JOBS_LIST_CONTROLS_KEYWORD_SEARCH}
                listTitle="Jobs"
                listDataFilters={seriesSearch}
                objectType="job"
                objectCreateRoute={createRoute}
                objectIndexName="Job"
                objectName="Job"
                defaultActivePhase="open"
                isRowClickable={hasPermission("jobs_view_permission", PERMISSION_LEVEL.FULL)}
                isEmptyStateCreateButtonVisible={hasPermission("jobs_create_permission", PERMISSION_LEVEL.RESTRICTED)}
            >
                <CardsView.Provider attributes={JOB_CARDS_VIEW_ATTRIBUTES} sortOptions={JOB_CARDS_VIEW_SORT_OPTIONS}>
                    <div onMouseEnter={() => setHoveredJobId("")}>
                        <ViewBar.Root>
                            <ViewBar.Start>
                                <ViewBar.Phases>
                                    <Table.Phase phase="open" title="Open" />
                                    <Table.Phase phase="closed" title="Closed" />
                                </ViewBar.Phases>
                                <ViewBar.VerticalDivider />
                                <JobTableViewTabs />
                            </ViewBar.Start>
                            <ViewBar.End>
                                <Table.SearchInput />
                                <ViewMenu />
                            </ViewBar.End>
                        </ViewBar.Root>
                    </div>
                    <CardList CardComponent={JobListCard} onCardHover={(id) => setHoveredJobId(id || "")} />
                </CardsView.Provider>
            </Table.Provider>
            <JobPreviewCard
                jobId={hoveredJobId || ""}
                isVisible={!!hoveredJobId && isPreviewModeEnabled}
                isPositionFixed={true}
            />
        </div>
    )
}
